/**
 * Subdomain detection and routing utilities for multi-tenant architecture
 * 
 * Handles:
 * - store.blazepos.app → subdomain = "store"
 * - blazepos.app → no subdomain (show store selector)
 * - www.blazepos.app → no subdomain (show store selector)
 * - localhost:5173 → development mode (check query param or env)
 */

const PRODUCTION_DOMAIN = 'blazepos.app';
const DEV_HOSTS = ['localhost', '127.0.0.1'];

/**
 * Check if running in Electron (file:// protocol or electronAPI available)
 */
export function isElectron(): boolean {
  return window.location.protocol === 'file:' || !!(window as any).electronAPI;
}

/**
 * Get the correct asset path for the current environment
 * In Electron (file:// protocol), paths need to be relative
 */
export function getAssetPath(absolutePath: string): string {
  if (isElectron() && absolutePath.startsWith('/')) {
    return '.' + absolutePath;
  }
  return absolutePath;
}

/**
 * Extract the subdomain from the current hostname
 * @returns The subdomain slug, or null if on apex domain / no subdomain
 */
export function getSubdomain(): string | null {
  const hostname = window.location.hostname;
  
  // Electron app - check localStorage for org slug (no subdomains in file:// protocol)
  if (isElectron()) {
    try {
      const lastOrg = localStorage.getItem('blazepos-dev-org');
      if (lastOrg) return lastOrg;
    } catch {
      // Ignore localStorage errors
    }
    return null;
  }
  
  // Development mode - check for ?org= query param
  if (DEV_HOSTS.some(host => hostname.includes(host))) {
    const params = new URLSearchParams(window.location.search);
    const orgParam = params.get('org');
    if (orgParam) {
      return orgParam;
    }
    // In dev without org param, check localStorage for last used org
    try {
      const lastOrg = localStorage.getItem('blazepos-dev-org');
      if (lastOrg) return lastOrg;
    } catch {
      // Ignore localStorage errors
    }
    return null;
  }
  
  // Production: Check if we're on the production domain
  if (hostname.endsWith(PRODUCTION_DOMAIN)) {
    // Extract subdomain: "store.blazepos.app" → "store"
    const parts = hostname.replace(`.${PRODUCTION_DOMAIN}`, '').split('.');
    
    // If hostname is exactly the production domain, no subdomain
    if (hostname === PRODUCTION_DOMAIN) {
      return null;
    }
    
    // If hostname is www.blazepos.app, treat as no subdomain
    if (parts.length === 1 && parts[0] === 'www') {
      return null;
    }
    
    // Return the subdomain (first part)
    if (parts.length >= 1 && parts[0] && parts[0] !== 'www') {
      return parts[0];
    }
  }
  
  // For other domains (custom domains), try to extract from hostname
  // This allows for future custom domain support
  const parts = hostname.split('.');
  if (parts.length > 2) {
    // e.g., "store.customdomain.com" → "store"
    return parts[0];
  }
  
  return null;
}

/**
 * Redirect the browser to a subdomain
 * @param slug The organization slug to redirect to
 */
export function redirectToSubdomain(slug: string): void {
  const currentProtocol = window.location.protocol;
  const targetUrl = `${currentProtocol}//${slug}.${PRODUCTION_DOMAIN}`;
  
  // Use replace to avoid back button issues
  window.location.replace(targetUrl);
}

/**
 * Redirect to the apex domain (store selector)
 */
export function redirectToApexDomain(): void {
  const currentProtocol = window.location.protocol;
  const targetUrl = `${currentProtocol}//${PRODUCTION_DOMAIN}`;
  
  window.location.replace(targetUrl);
}

/**
 * Check if we're in development mode (includes Electron desktop app)
 */
export function isDevelopment(): boolean {
  // Electron app uses file:// protocol - treat as development for routing purposes
  if (isElectron()) {
    return true;
  }
  const hostname = window.location.hostname;
  return DEV_HOSTS.some(host => hostname.includes(host));
}

/**
 * Set the development org for local testing
 * @param slug The organization slug to use in development
 */
export function setDevOrg(slug: string): void {
  try {
    localStorage.setItem('blazepos-dev-org', slug);
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Clear the development org
 */
export function clearDevOrg(): void {
  try {
    localStorage.removeItem('blazepos-dev-org');
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Get the full URL for a given subdomain
 * @param slug The organization slug
 * @returns The full URL for that subdomain
 */
export function getSubdomainUrl(slug: string): string {
  if (isDevelopment()) {
    // In development, use query param
    const baseUrl = window.location.origin;
    return `${baseUrl}?org=${slug}`;
  }
  
  return `https://${slug}.${PRODUCTION_DOMAIN}`;
}

